import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { UserPermissionEditSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";
import DesignationModal from "./designation";
import "./um.css";

// react-select themed to the design tokens (light + dark)
const colourStyles = {
  control: (styles, state) => ({
    ...styles,
    minHeight: 42,
    backgroundColor: "var(--color-surface)",
    borderColor: state.isFocused ? "var(--color-primary)" : "var(--color-border)",
    borderRadius: 10,
    boxShadow: state.isFocused ? "0 0 0 3px var(--color-primary-weak)" : "none",
    "&:hover": { borderColor: "var(--color-primary)" },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    zIndex: 30,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "var(--color-primary-weak)",
    borderRadius: 8,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "var(--color-primary)",
    fontWeight: 700,
    fontSize: 12,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "var(--color-primary)",
    borderRadius: 8,
    ":hover": { backgroundColor: "var(--color-hover)", color: "var(--color-primary)" },
  }),
  singleValue: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  input: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  placeholder: (styles) => ({ ...styles, color: "var(--color-text-tertiary)" }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isFocused ? "var(--color-hover)" : "var(--color-surface)",
    color: "var(--color-text-primary)",
    ":active": { backgroundColor: "var(--color-hover)" },
  }),
};

const UserManagementEdit = () => {
  const Navigate = useNavigate();
  const location = useLocation();

  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    module_id: [],
    sub_module_id: [],
  });

  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hardcoded designation data
  const [designationData] = useState([
    { value: "1", label: "Graphic Designer" },
    { value: "2", label: "Sales Executive" },
  ]);

  const [moduleData] = useState([
    { label: "Dashboard", value: 1 },
    { label: "User Management", value: 2 },
    { label: "Buyer Management", value: 3 },
    { label: "Supplier Management", value: 4 },
    { label: "Design Studio", value: 5 },
    { label: "Assigned Orders", value: 6 },
    { label: "GD Management", value: 7 },
    { label: "Master", value: 8 },
  ]);

  // Hardcoded submodule data
  const [allSubModuleData] = useState([
    { sub_module_id: 1, module_id: 1, name: "Dashboard" },
    { sub_module_id: 2, module_id: 2, name: "User Management" },
    { sub_module_id: 3, module_id: 3, name: "Buyer Lead" },
    { sub_module_id: 4, module_id: 3, name: "Request Quotation" },
    { sub_module_id: 5, module_id: 3, name: "Supplier Buy Lead" },
    { sub_module_id: 6, module_id: 4, name: "Supplier" },
    { sub_module_id: 7, module_id: 4, name: "Connection" },
    { sub_module_id: 8, module_id: 4, name: "Product" },
    { sub_module_id: 9, module_id: 5, name: "Design Studio" },
    { sub_module_id: 10, module_id: 6, name: "Assigned Orders" },
    { sub_module_id: 11, module_id: 7, name: "List" },
    { sub_module_id: 12, module_id: 8, name: "Product" },
    { sub_module_id: 13, module_id: 8, name: "Category" },
    { sub_module_id: 14, module_id: 8, name: "Sub Category" },
    { sub_module_id: 15, module_id: 8, name: "Blog" },
    { sub_module_id: 16, module_id: 8, name: "Blog Category" },
    { sub_module_id: 17, module_id: 8, name: "RFQs" },
    { sub_module_id: 18, module_id: 8, name: "Consultations" },
    { sub_module_id: 19, module_id: 7, name: "Connection" },
  ]);

  const [subModuleData, setSubModuleData] = useState([]);

  const handleAddDesignation = (newOption) => {};

  // Function to structure modules data for API
  const structureModulesData = (moduleIds, subModuleIds) => {
    const modulesMap = {};

    subModuleIds.forEach((subModuleId) => {
      const subModule = allSubModuleData.find(
        (item) => item.sub_module_id === subModuleId
      );
      if (subModule) {
        if (!modulesMap[subModule.module_id]) {
          modulesMap[subModule.module_id] = [];
        }
        modulesMap[subModule.module_id].push(subModuleId);
      }
    });

    const modules = moduleIds
      .filter(
        (moduleId) => modulesMap[moduleId] && modulesMap[moduleId].length > 0
      )
      .map((moduleId) => ({
        module_id: moduleId,
        sub_module_ids: modulesMap[moduleId],
      }));

    return modules;
  };

  // Fetch user data and permissions for editing
  useEffect(() => {
    if (!location?.state?.user_id) {
      console.error("No user_id found in location state");
      toast.error("User ID not found");
      Navigate("/admin/UserManagement/list");
      return;
    }

    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const user_id = location.state.user_id;
        setUserId(user_id);

        // Fetch user permissions data
        const res = await axios.get(
          `${process.env.REACT_APP_URL}/admin/sub_admin_module_permissions`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );

        let permissions = res?.data?.data?.permissions || [];

        // ✅ Filter by user_id
        permissions = permissions.filter((item) => item.user_id === user_id);

        if (permissions.length > 0) {
          const firstPermission = permissions[0];

          // Extract unique module IDs and sub-module IDs
          const uniqueModuleIds = [
            ...new Set(
              permissions.map((item) => item.module_id).filter(Boolean)
            ),
          ];
          const uniqueSubModuleIds = [
            ...new Set(
              permissions.map((item) => item.sub_module_id).filter(Boolean)
            ),
          ];

          const newValues = {
            name:
              firstPermission?.subAdmin?.name || firstPermission?.name || "",
            email: firstPermission?.subAdmin?.email || "",
            phone: firstPermission?.subAdmin?.phone || "",
            designation: firstPermission?.designation || "",
            module_id: uniqueModuleIds,
            sub_module_id: uniqueSubModuleIds,
          };

          setInitialValues(newValues);
        } else {
          toast.error("No permissions found for this user");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        toast.error("Failed to load user data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [location.state, Navigate]);

  const EditUser = useFormik({
    initialValues,
    validationSchema: UserPermissionEditSchema,
    enableReinitialize: true,

    onSubmit: (values, { setSubmitting }) => {
      if (!userId) {
        toast.error("User ID not found");
        setSubmitting(false);
        return;
      }

      // Structure the modules data according to API requirements
      const modulesData = structureModulesData(
        values.module_id,
        values.sub_module_id
      );

      // Prepare the data for submission according to API structure
      const submitData = {
        designation: values.designation,
        modules: modulesData,
      };

      // Use PUT request with the user ID in the URL
      axios
        .put(
          `${process.env.REACT_APP_URL}/admin/sub_admin_module_permissions/${userId}`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        )
        .then((res) => {
          if (res.status === 200) {
            toast.success(`${res?.data?.message}`);
            Navigate("/admin/UserManagement/list", { state: res.data.message });
          }
        })
        .catch((err) => {
          console.error("API Error:", err.response?.data);
          toast.error(err.response?.data?.message || "Error updating data");
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  // Update SubModule options based on selected Modules
  useEffect(() => {
    if (EditUser?.values?.module_id?.length > 0) {
      const filteredSubModules = allSubModuleData
        .filter((item) => EditUser.values.module_id.includes(item.module_id))
        .map((item) => ({
          label: item?.name || "Untitled",
          value: item?.sub_module_id,
        }));

      setSubModuleData(filteredSubModules);

      // Also filter out any selected submodules that are no longer in the filtered list
      const currentSubModules = EditUser.values.sub_module_id;
      const validSubModules = currentSubModules.filter((subModuleId) =>
        filteredSubModules.some((item) => item.value === subModuleId)
      );

      if (validSubModules.length !== currentSubModules.length) {
        EditUser.setFieldValue("sub_module_id", validSubModules);
      }
    } else {
      setSubModuleData([]);
      if (EditUser.values.sub_module_id.length > 0) {
        EditUser.setFieldValue("sub_module_id", []);
      }
    }
  }, [EditUser.values.module_id, allSubModuleData]);

  if (isLoading) {
    return (
      <div className="um">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 240 }}
        >
          <span className="uk-spinner uk-spinner--lg" />
        </div>
      </div>
    );
  }

  const err = (k) =>
    EditUser.errors[k] && EditUser.touched[k] ? (
      <span className="um-err">{EditUser.errors[k]}</span>
    ) : null;

  return (
    <div className="um">
      <Toaster position="top-center" reverseOrder={false} />

      <form onSubmit={EditUser.handleSubmit}>
        {/* page header */}
        <div className="um-head">
          <div>
            <h1 className="um-title">Sub Admin Edit</h1>
            <p className="um-sub">
              Update designation and module access
              {EditUser.values.name ? (
                <>
                  {" "}
                  for <strong>{EditUser.values.name}</strong>
                </>
              ) : null}
              .
            </p>
          </div>
          {EditUser.dirty && (
            <div className="um-unsaved">
              <span className="um-unsaved__dot" />
              Unsaved changes
            </div>
          )}
        </div>

        {/* Account Details (read-only) */}
        <div className="um-card">
          <div className="um-card__head">
            <span className="um-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
            <div>
              <h3 className="um-card__title">Account Details</h3>
              <p className="um-card__sub">Read-only identity from registration.</p>
            </div>
          </div>
          <div className="um-card__body um-grid">
            <div className="um-field">
              <label className="um-label">Name</label>
              <input className="um-input um-input--readonly" value={EditUser.values.name} name="name" readOnly />
            </div>
            <div className="um-field">
              <label className="um-label">Email</label>
              <input className="um-input um-input--readonly" value={EditUser.values.email} name="email" readOnly />
            </div>
            <div className="um-field">
              <label className="um-label">Contact Number</label>
              <input className="um-input um-input--readonly um-mono" value={EditUser.values.phone} name="phone" readOnly />
            </div>
            <div className="um-field">
              <label className="um-label">Role</label>
              <input className="um-input um-input--readonly" value="subAdmin" readOnly />
            </div>
          </div>
        </div>

        {/* Access & Permissions */}
        <div className="um-card">
          <div className="um-card__head">
            <span className="um-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            <div>
              <h3 className="um-card__title">Access &amp; Permissions</h3>
              <p className="um-card__sub">Control which modules and sub-modules this sub-admin can use.</p>
            </div>
          </div>
          <div className="um-card__body um-grid">
            <div className="um-field">
              <label className="um-label">
                Designation
                <button
                  type="button"
                  className="um-addbtn"
                  title="Add designation"
                  onClick={() => setShowDesignationModal(true)}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </label>
              <ReactSelect
                options={designationData}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Designation"
                name="designation"
                onChange={(selectedOption) =>
                  EditUser.setFieldValue("designation", selectedOption ? selectedOption.value : "")
                }
                onBlur={EditUser.handleBlur}
                value={designationData.find((item) => item.label === EditUser.values.designation) || null}
              />
              {err("designation")}
            </div>
            <div className="um-field">
              <label className="um-label">Module</label>
              <ReactSelect
                isMulti
                options={moduleData}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Module"
                name="module_id"
                closeMenuOnSelect={false}
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions ? selectedOptions.map((item) => item.value) : [];
                  EditUser.setFieldValue("module_id", selectedValues);
                }}
                onBlur={EditUser.handleBlur}
                value={moduleData.filter((item) => EditUser.values.module_id.includes(item.value))}
              />
              {err("module_id")}
            </div>
            <div className="um-field um-field--full">
              <label className="um-label">Sub Module</label>
              <ReactSelect
                isMulti
                options={subModuleData}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Sub Module"
                name="sub_module_id"
                closeMenuOnSelect={false}
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions ? selectedOptions.map((item) => item.value) : [];
                  EditUser.setFieldValue("sub_module_id", selectedValues);
                }}
                onBlur={EditUser.handleBlur}
                value={subModuleData.filter((item) => EditUser.values.sub_module_id.includes(item.value))}
              />
              {err("sub_module_id")}
            </div>
          </div>
        </div>

        <DesignationModal
          show={showDesignationModal}
          onClose={() => setShowDesignationModal(false)}
          onAddSize={handleAddDesignation}
        />

        {/* sticky action bar */}
        <div className="um-actions-bar">
          <div className="um-actions-bar__hint">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Permissions take effect on the sub-admin's next login.
          </div>
          <div className="um-actions-bar__btns">
            <button type="button" className="um-btn" onClick={() => Navigate(-1)}>
              Cancel
            </button>
            <button
              type="submit"
              className="um-btn um-btn--primary"
              disabled={EditUser.isSubmitting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {EditUser.isSubmitting ? "Updating…" : "Update"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserManagementEdit;
